// Single source of truth for the consultation status lifecycle on the frontend.
// Mirrors api/src/utils/consultationStatus.js — keep the two in sync.
// See docs/specs/consultation-status-lifecycle.md.

import type { SelectOption } from '../components/Select';

export type ConsultationStatus =
  | 'scheduled'
  | 'awaiting_payment'
  | 'waitlist'
  | 'paid'
  | 'not_interested'
  | 'cancelled';

// Ordered by lifecycle position.
export const STATUSES: ConsultationStatus[] = [
  'scheduled',
  'awaiting_payment',
  'waitlist',
  'paid',
  'not_interested',
  'cancelled',
];

export const STATUS_LABELS: Record<ConsultationStatus, string> = {
  scheduled: 'Scheduled',
  awaiting_payment: 'Awaiting Payment',
  waitlist: 'Waitlist',
  paid: 'Paid',
  not_interested: 'Not Interested',
  cancelled: 'Cancelled',
};

export const STATUS_MEANINGS: Record<ConsultationStatus, string> = {
  scheduled: "Consultation is booked / has happened; the outcome isn't decided yet. Start state only.",
  awaiting_payment: "Prospect decided to enroll; we're waiting for the payment to land.",
  waitlist:
    "Prospect wants in, but there's no open seat or the schedule doesn't fit — a capacity/offer issue, not a lack of interest.",
  paid: 'Prospect paid and converted to a student.',
  not_interested:
    'Prospect is not interested in enrolling — a genuine interest decline (distinct from Waitlist).',
  cancelled: "The consultation was called off / didn't happen.",
};

/**
 * `scheduled` is start-only: allowed only when the consultation is at its start
 * (`from == null`) or is already `scheduled`. Never reopen a decided
 * consultation back into `scheduled`.
 */
export function isValidTransition(
  from: ConsultationStatus | null | undefined,
  to: ConsultationStatus
): boolean {
  if (!STATUSES.includes(to)) return false;
  if (to === 'scheduled' && from != null && from !== 'scheduled') return false;
  return true;
}

/** Statuses that may legally be selected given the current one. */
export function allowedTargets(
  current: ConsultationStatus | null | undefined
): ConsultationStatus[] {
  return STATUSES.filter((to) => isValidTransition(current, to));
}

export function statusLabel(status: ConsultationStatus | null | undefined): string {
  return status ? STATUS_LABELS[status] : 'Not set';
}

// Lost-reason capture (only when status === 'not_interested').
export const LOST_REASONS: SelectOption<string>[] = [
  { value: 'no_group_for_level', label: 'No suitable group for their level' },
  { value: 'schedule_conflict', label: "Schedule didn't fit" },
  { value: 'price_too_high', label: 'Price too high' },
  { value: 'chose_competitor', label: 'Chose another school' },
  { value: 'not_ready_timing', label: 'Not ready to commit (timing)' },
  { value: 'prefers_private', label: 'Prefers private 1-on-1 lessons' },
  { value: 'went_silent', label: 'Went silent / no response' },
  { value: 'other', label: 'Other' },
];

export const LOST_REASON_OTHER_MAXLEN = 80;

export function lostReasonLabel(key: string | null | undefined): string {
  if (!key) return '';
  return LOST_REASONS.find((r) => r.value === key)?.label ?? key;
}

/** Chart colors per status (Stats → Consultations). Paid matches "Paid / $100" on Ad Efficiency. */
export const STATUS_CHART_COLORS: Record<ConsultationStatus, string> = {
  scheduled: '#4299e1',
  awaiting_payment: '#d69e2e',
  waitlist: '#805ad5',
  paid: '#2f855a',
  not_interested: '#e53e3e',
  cancelled: '#a0aec0',
};
